import React, { FunctionComponent, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import checkPrimaryImage from '../../assets/check-primary.png';
import { Button } from '../Input/Button/Button';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { DealerLaunchSidebar } from './DealerLaunchSidebar';
import { DealerLaunchSection, DealerLaunchStep } from './types';
import { Dealer } from '../../types/dealerTypes';
import { LinkButton } from '../Input/Button/LinkButton';
import styles from './styles.module.scss';
import { shouldHideSectionOrStep } from './steps';

type Props = {
  dealer: Dealer;
  error?: unknown;
  hideReviewNav?: boolean;
  skipStep?: () => void;
  showAdminView?: boolean;
} & (
  | {
      // either step of section should be defined
      step: DealerLaunchStep;
      section?: undefined;
    }
  | {
      step?: undefined;
      section: DealerLaunchSection;
    }
);

export const DealerLaunchPageFrame: FunctionComponent<Props> = ({
  step,
  section,
  dealer,
  error,
  skipStep,
  hideReviewNav,
  children,
  showAdminView,
}) => {
  // update the window title based on the step/section name
  useEffect(() => {
    if (!step && !section) return undefined;
    const currentPageTitle = document.title;
    document.title = `${
      step?.name || section?.name
    } | Upstart Auto Retail Self-Service`;
    return () => {
      document.title = currentPageTitle;
    };
  }, [section, step]);

  const stepSectionsToShow = useMemo(
    () =>
      step?.section?.steps
        .filter(
          (stepItem) =>
            !shouldHideSectionOrStep(stepItem, dealer) && !stepItem.hideStepNav,
        )
        .filter((s) => (s.shouldHideStep ? !s.shouldHideStep(dealer) : true)),
    [step, dealer],
  );

  const renderStepNav = () => {
    if (!stepSectionsToShow) return null;
    return (
      <div className={styles.stepNav}>
        {stepSectionsToShow &&
          stepSectionsToShow.map((sectionStep, index) => (
            <React.Fragment key={sectionStep.route.template}>
              <LinkButton
                className={classNames(
                  styles.stepNavButton,
                  sectionStep.isComplete(dealer) &&
                    styles.stepNavButtonComplete,
                )}
                to={sectionStep.route.getPath(dealer)}
                variant="minimal"
              >
                <img src={checkPrimaryImage} alt="Complete" />
                {sectionStep.breadcrumbName || sectionStep.name}
              </LinkButton>
              {index !== stepSectionsToShow.length - 1 && (
                <span className={styles.stepNavButtonSeparator}>›</span>
              )}
            </React.Fragment>
          ))}
      </div>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <DealerLaunchSidebar
        dealer={dealer}
        currentSection={step?.section || section}
        hideSectionNav={step?.hideSectionNav}
        hideHomeNav={step?.hideHomeNav}
        hideReviewNav={step?.hideReviewNav || hideReviewNav}
        showAdminView={showAdminView}
      />
      <div className={styles.sideBarOffset} />
      <div className={styles.contentContainer}>
        <ErrorMessage error={error} />
        {!step?.hideStepNav && renderStepNav()}
        {children}
        <div className="spacer" />
        <div className={styles.contentNav}>
          <div className="spacer" />
          {step && !step.hideSkipStep && (
            <Button label="Skip Step >>" variant="minimal" onClick={skipStep} />
          )}
        </div>
      </div>
    </div>
  );
};
